import Images from "assets/images"
import {
    SilSafetyFunction,
    SilResultSafetyFunction,
    SilResultProjectDevice,
    MtProject,
} from "server/models"
import { ASC } from "const"
import { IMAGE_BASE64_TYPE, DEVICE_REFERENCE_TYPE } from "client/utils"
import { sort } from "client/store/utils"
import {
    ModuleList,
    AccountSettings,
    ProjectSettings,
    UserSettings,
    UserData,
    LangType,

    LoadAllSilSafetyFunctions,
    LoadSilResultSafetyFunctionById,
    LoadQidDatapointById,
    LoadDefaultQidProductDocument,

    GetFile,
} from "client/store"

import pdfmake from "client/components/pdfmake/pdfmake"
import vfs from "client/components/pdfmake/vfs_fonts"
import fontList from "client/components/pdfmake/fontList"

import {
    ReportAccountLogo,
    ReportProjectLogo,
} from "client/components/pdfmake/template/functions"
import * as Const from "client/components/pdfmake/template/const"
import templateStyles from "client/components/pdfmake/template/styles"

import { Header, Footer } from "./headerFooter"
import TitlePage from "./titlePage"
import CopyrightPage from "./copyrightPage"
import OverviewPage from "./overviewPage"
import SafetyFunctionsPage from "./safetyFunctionsPage"
import AppendixA from "./appendixA"
import AppendixB from "./appendixB"


pdfmake.addVirtualFileSystem(vfs)
pdfmake.addFonts(fontList)


const ExportToPDF = (
    project: MtProject,
    moduleList: ModuleList,
    accountSettings: AccountSettings,
    projectSettings: ProjectSettings,
    userSettings: UserSettings,
    userData: UserData,
    lang: LangType,
    callback: (isLoading: boolean) => void,
) => {
    callback(true)

    LoadAllSilSafetyFunctions(project.id).then((res1) => {
        const safetyFunctions = (Object.values(res1) as SilSafetyFunction[]).sort((a, b) => sort(a, b, "name", ASC))
        const calculatedSafetyFunctions: { [safetyFunctionId: number]: SilResultSafetyFunction } = {}

        const calculatedPromiseArray: Promise<any>[] = []
        safetyFunctions.forEach((safetyFunction) => {
            calculatedPromiseArray.push(
                LoadSilResultSafetyFunctionById(safetyFunction.id, project.id).then((res2) => {
                    if (res2 && res2.id) calculatedSafetyFunctions[safetyFunction.id] = res2
                }),
            )
        })

        Promise.all(calculatedPromiseArray).then(() => {
            let accountLogo: string
            let projectLogo: string

            // load all used images as base64 before creating pdf
            const promiseArray: Promise<any>[] = [
                ReportAccountLogo(userData).then((res) => {
                    accountLogo = res
                }),
                ReportProjectLogo(project, userSettings).then((res) => {
                    projectLogo = res
                }),
            ]


            // load device logos as base64 and device references
            const deviceImageBase64: IMAGE_BASE64_TYPE = {}
            const uniqueDeviceList: { [deviceId: number]: SilResultProjectDevice } = {}
            const deviceReferenceList: DEVICE_REFERENCE_TYPE = {}

            safetyFunctions.forEach((safetyFunction) => {
                const resultSafetyFunction = calculatedSafetyFunctions[safetyFunction.id]
                if (resultSafetyFunction) {
                    const subsystemList = resultSafetyFunction.subsystems || []
                    subsystemList.forEach((subsystem) => {
                        subsystem.blocks.forEach((block) => {
                            const { projectDevice } = block
                            if (projectDevice) uniqueDeviceList[projectDevice.id] = projectDevice
                        })
                    })
                }
            })

            const deviceArrayOrdered: SilResultProjectDevice[] = Object.values(uniqueDeviceList).sort((a, b) => sort(a, b, "name", ASC))
            const defaultQidProductLogo = `${window.location.origin}${Images.qidProductLogo}`
            const defaultSilDeviceLogo = `${window.location.origin}${Images.silDeviceLogo}`

            deviceArrayOrdered.forEach((device) => {
                // load device logo
                const defaultLogo = device.qidProductId ? defaultQidProductLogo : defaultSilDeviceLogo

                promiseArray.push(
                    GetFile(device.logo, defaultLogo).then((res) => {
                        deviceImageBase64[device.id] = res
                    }),
                )

                // load reference document
                if (device.qidDatapointId) {
                    promiseArray.push(
                        // load datapoint reference
                        LoadQidDatapointById(device.qidDatapointId).then((datapoint) => {
                            if (datapoint && datapoint.reference) {
                                deviceReferenceList[device.id] = datapoint.reference
                                return null
                            }

                            // if reference is null - load default QID product document
                            return LoadDefaultQidProductDocument(device.qidProductId).then((reference) => {
                                deviceReferenceList[device.id] = reference
                            })
                        }),
                    )
                } else if (device.qidProductId) {
                    promiseArray.push(
                        // load default QID product document
                        LoadDefaultQidProductDocument(device.qidProductId).then((reference) => {
                            deviceReferenceList[device.id] = reference
                        }),
                    )
                } else {
                    // account/project device
                    deviceReferenceList[device.id] = device.reference
                }
            })


            Promise.all(promiseArray).then(() => {
                const docDefinition = {
                    info: {
                        title: lang.silCalculationReport,
                        author: lang.risknowlogy,
                        subject: lang.result,
                        keywords: "SIL, PDF, Calculation, Risknowlogy",
                    },
                    pageMargins: Const.PAGE_MARGINS,
                    pageSize: "A4",
                    images: {
                        accountLogo,
                        projectLogo,
                    },
                    defaultStyle: {
                        fontSize: 11,
                        font: "Arial",
                    },
                    styles: templateStyles,
                    header: (currentPage: number) => Header(currentPage, project),
                    footer: (currentPage: number, pageCount: number) => (
                        Footer(currentPage, pageCount, project, userData, lang)
                    ),
                    content: TitlePage(project, lang).concat(
                        CopyrightPage(moduleList, lang),
                        [{
                            pageBreak: "before",
                            toc: {
                                title: {
                                    text: lang.tableOfContents,
                                    style: Const.HEADER,
                                    tocItem: true,
                                    tocStyle: Const.TOC_HEADER,
                                    tocMargin: [0, 5, 0, 0],
                                },
                                numberStyle: {
                                    bold: true,
                                    fontSize: 11,
                                },
                            },
                        }],
                        OverviewPage(safetyFunctions, project, lang),
                        SafetyFunctionsPage(safetyFunctions, calculatedSafetyFunctions, deviceImageBase64, userSettings, lang),
                        AppendixA(deviceArrayOrdered, deviceImageBase64, deviceReferenceList, userSettings, lang),
                        AppendixB(accountSettings, projectSettings, lang),
                    ),
                }

                callback(false)
                pdfmake.createPdf(docDefinition).open()
            })
        })
    })
}
export default ExportToPDF
